import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Table,
  Tag,
  Modal,
  Form,
  Select,
  Space,
  Input,
  Switch,
  message,
  Divider,
  Descriptions,
  Spin,
  Avatar,
} from "antd";
import {
  NotificationOutlined,
  SendOutlined,
  EyeOutlined,
  ReloadOutlined,
  UserOutlined,
  FilterFilled,
} from "@ant-design/icons";
import moment from "moment";
import useRequest from "../hooks/useRequest";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

function NotificationManager() {
  const { request } = useRequest();
  const [form] = Form.useForm();

  const isInitialRender = useRef(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filtersVisible, setFiltersVisible] = useState(true);

  // User pagination & search state
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const searchTimeoutRef = useRef(null);

  // ===============================
  // FETCH USERS
  // ===============================
  const fetchUsers = useCallback((page = 1, search = "", append = false) => {
    setUserLoading(true);

    const params = new URLSearchParams({
      page: page,
      limit: 10,
    });
    if (search) params.append("search", search);

    request({
      url: `/api/admin/users?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => {
        setUserLoading(false);
        const newData = res?.data || [];
        
        if (append) {
          setUsers((prev) => [...prev, ...newData]);
        } else {
          setUsers(newData);
        }

        // Check if we have more users
        const totalPages = res?.pagination?.totalPages || 1;
        setHasMoreUsers(page < totalPages);
      },
      onError: () => {
        setUserLoading(false);
        message.error("Failed to fetch users");
      },
    });
  }, [request]);

  const handleUserSearch = (value) => {
    setUserSearch(value);
    setUserPage(1);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchUsers(1, value, false);
    }, 500); // 500ms debounce
  };

  const handleUserScroll = (e) => {
    const { target } = e;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 5 &&
      hasMoreUsers &&
      !userLoading
    ) {
      const nextPage = userPage + 1;
      setUserPage(nextPage);
      fetchUsers(nextPage, userSearch, true);
    }
  };

  // ===============================
  // FETCH NOTIFICATIONS
  // ===============================
  const fetchNotifications = useCallback(() => {
    setLoading(true);

    request({
      url: `/api/admin/notification?page=${pagination.current}&limit=${pagination.pageSize}`,
      method: "GET",
      onSuccess: (res) => {
        setNotifications(res?.data || []);
        const meta = res?.pagination || {}
        setPagination((prev) => ({
          ...prev,
          total: meta.total || 0,
          current: meta.page || prev.current,
          pageSize: meta.limit || prev.pageSize,
        }));
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        setUsers([]);
        setNotifications([])
        setPagination((prev) => ({ ...prev, total: 0 }));
        message.error("Failed to fetch notifications");
      },
    });
  }, [request]);

  useEffect(() => {
    fetchNotifications();
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (modalOpen && users.length === 0) {
      setUserSearch("");
      setUserPage(1);
      fetchUsers(1, "", false);
    }
  }, [modalOpen]);

  // ===============================
  // SEND NOTIFICATION
  // ===============================
  const handleSend = (values) => {
    if (!values.title || !values.message) {
      message.error("Title and message are required");
      return;
    }
    setLoading(true);
    const isEmailEnabled = !!values.email;
    const emailSubject = values.email_subject?.trim();

    request({
      url: "/api/admin/notification/send",
      method: "POST",
      data: {
        recipient_id: values.user,
        title: values.title,
        message: values.message,
        ...(isEmailEnabled ? { email_subject: emailSubject } : {}),
        meta: {
          tts: values.tts || false,
          email: isEmailEnabled,
        },
      },
      onSuccess: () => {
        setLoading(false);
        message.success("Notification sent successfully");
        form.resetFields();
        setModalOpen(false);
        fetchNotifications();
      },
      onError: () => {
        setLoading(false);
        message.error("Failed to send notification");
      },
    });
  };

  // ===============================
  // TABLE COLUMNS
  // ===============================
  const columns = [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    // {
    //   title: "Title",
    //   dataIndex: "title",
    //   key: "title",
    //   width: 120,
    //   render: (title) => <Text code>{title}</Text>,
    // },
    {
      title: "Recipient",
      width: 200,
      render: (_, record) => (
        <>
          <div>{record.recipient_user_name || "-"}</div>
          <Text type="secondary">ID: {record?.id}</Text>
        </>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Date & Time",
      width: 200,
      render: (_, record) =>
        moment(record.created_at).format("DD MMM YYYY, hh:mm A"),
    },
    {
      title: "Status",
      width: 200,
      render: () => <Tag color="green">Sent</Tag>,
    },
    {
      title: "Action",
      width: 200,
      render: (_, record) => (
        <Button
          className="button-action-small"
          onClick={() => {
            setSelectedNotification(record);
            setViewOpen(true);
          }}
        >
          <EyeOutlined />
        </Button>
      ),
    },
  ];

  return (
    <div
      style={{
        background: "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      {/* HEADER */}
      <Card
        style={{
          borderRadius: 20,
          marginBottom: 25,
          boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <NotificationOutlined /> Notification Manager
            </Title>
            <Text type="secondary">
              Send notifications and track delivery history
            </Text>
          </Col>

          <Col>
            <Button
              type="primary"
              size="large"
              icon={<SendOutlined />}
              onClick={() => setModalOpen(true)}
              style={{ borderRadius: 10 }}
            >
              Send Notification
            </Button>
          </Col>
        </Row>
      </Card>

      {/* TABLE */}
      <Card
        style={{
          borderRadius: 20,
          boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
        }}
      >
        <div className="table-responsive">
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={notifications}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                onChange: (page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize,
                  }));
                },
              }}
            />
          </Spin>
        </div>
      </Card>

      {/* SEND MODAL */}
      <Modal
        title="Send Notification"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setUserSearch("");
          setUserPage(1);
        }}
        footer={null}
        width={600}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSend}
          onValuesChange={(changedValues) => {
            if (Object.prototype.hasOwnProperty.call(changedValues, "email") && !changedValues.email) {
              form.setFieldValue("email_subject", undefined);
            }
          }}
        >
          {/* USER SELECT */}
          <Form.Item
            name="user"
            label="Select User"
            rules={[{ required: true }]}
          >
            <Select
              size="large"
              loading={userLoading}
              showSearch
              allowClear
              optionLabelProp="label"
              placeholder="Search and select user"
              onSearch={handleUserSearch}
              onPopupScroll={handleUserScroll}
              filterOption={false}
              searchValue={userSearch}
            >
              <Option key="all" value="all" label="Send to All Users">
                <Space>
                  <Avatar icon={<UserOutlined />} />
                  <span style={{ fontWeight: 500 }}>Send to All Users</span>
                </Space>
              </Option>

              {users.map((user) => (
                <Option 
                  key={user.id} 
                  value={user.id} 
                  label={
                    <Space align="center">
                      <Avatar src={user.profile_img} icon={<UserOutlined />} size="small" />
                      <span>{user.full_name || user.email}</span>
                    </Space>
                  }
                >
                  <Space align="center">
                    <Avatar src={user.profile_img} icon={<UserOutlined />} />
                    <div style={{ lineHeight: '1.2' }}>
                      <div style={{ fontWeight: 500 }}>{user.full_name || "Unknown User"}</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {user.email} {user.mobile ? `• ${user.mobile}` : ""}
                      </Text>
                    </div>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true }]}
          >
            <Input placeholder="Enter title" />
          </Form.Item>

          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true }]}
          >
            <TextArea rows={4} />
          </Form.Item>

          {/* <Form.Item name="tts" label="Enable TTS" valuePropName="checked">
            <Switch />
          </Form.Item> */}

          <Form.Item name="email" label="Send Email" valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* <Form.Item shouldUpdate={(prev, current) => prev.email !== current.email} noStyle>
            {({ getFieldValue }) =>
              getFieldValue("email") ? (
                <Form.Item
                  name="email_subject"
                  label="Email Subject"
                  rules={[
                    { required: true, message: "Please enter email subject" },
                    {
                      validator: (_, value) => {
                        if (!value || !value.trim()) {
                          return Promise.reject(new Error("Email subject cannot be empty"));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="Enter email subject" />
                </Form.Item>
              ) : null
            }
          </Form.Item> */}

          <Button type="primary" htmlType="submit" block size="large">
            Send Now
          </Button>
        </Form>
      </Modal>

      {/* VIEW MODAL */}
      <Modal
        title={
          <Space align="center" size={10}>
            <EyeOutlined style={{ color: "#1677ff" }} />
            <span style={{ fontWeight: 700, fontSize: 20 }}>
              Notification Details
            </span>
          </Space>
        }
        open={viewOpen}
        onCancel={() => setViewOpen(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setViewOpen(false)}
            style={{ borderRadius: 10, fontWeight: 600 }}
          >
            Close
          </Button>,
        ]}
        width={600}
        styles={{
          content: {
            borderRadius: 16,
            overflow: "hidden",
          },
          header: {
            borderBottom: "1px solid #e5e7eb",
            marginBottom: 0,
            padding: "16px 20px",
            background: "#f8fafc",
          },
          body: {
            padding: 18,
            background: "#ffffff",
          },
          footer: {
            borderTop: "1px solid #e5e7eb",
            marginTop: 0,
            padding: "12px 18px",
          },
        }}
      >
        {selectedNotification && (
          <>
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                boxShadow: "0 6px 16px rgba(2,8,23,.06)",
              }}
              bodyStyle={{ padding: 14 }}
            >
              <Descriptions
                column={1}
                colon={false}
                labelStyle={{
                  width: 160,
                  color: "#64748b",
                  fontWeight: 600,
                }}
                contentStyle={{ color: "#0f172a", fontWeight: 500 }}
              >
                <Descriptions.Item label="Notification ID">
                  <Text code>{selectedNotification.id}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Title">
                  <Text>{selectedNotification.title}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Recipient">
                  {selectedNotification.recipient?.full_name || "-"}
                </Descriptions.Item>

                <Descriptions.Item label="Message">
                  {selectedNotification.message || "-"}
                </Descriptions.Item>

                <Descriptions.Item label="Created At">
                  {moment(selectedNotification.created_at).format(
                    "DD MMM YYYY, hh:mm A",
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                borderRadius: 10,
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
              }}
            >
              <Text type="secondary">Delivered via in-app notification.</Text>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default NotificationManager;
